import React, { useEffect, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Progress, ResponseErrorPanel } from '@backstage/core-components';
import { BDirectRow, useBDirectRows } from '../../hooks/useBDirectRows';
import { groupBySection, orphanSectionRow } from './ChecklistDetail/helpers';
import {
  PREPROD_REGION_COLUMNS,
  REGION_COLUMNS,
} from './BDirectSectionCard/types';
import { RegionCell } from './BDirectSectionCard/RegionCell';
import { PreprodRegionCell } from './BDirectSectionCard/PreprodRegionCell';
import { CurrentCell } from './BDirectSectionCard/CurrentCell';
import { STATE_LABEL } from './BDirectSectionCard/StateSelect';
import { ParkedTint, rowDisplayMeta } from './releaseTag';

// Strip the `kind:namespace/` prefix from an entity ref so the printed name
// reads as the bare user/group identifier without dragging the catalog
// addressing scheme onto paper. Also trims the `_bidone.co.nz` tenant
// suffix that user refs carry in this catalog, since every owner shares
// it and it just eats column width.
const refDisplay = (ref: string) => {
  const slash = ref.indexOf('/');
  const tail = slash >= 0 ? ref.slice(slash + 1) : ref;
  return tail.replace(/_bidone\.co\.nz$/i, '');
};

// Root container — combines the page chrome with the print-only overrides
// for the RegionCell Checkbox so the disabled checkmark survives a B&W
// print. Backstage's createUnifiedTheme prefixes MUI v5 classes with
// `v5-`, so we target `v5-MuiCheckbox-root`.
const Root = styled('div')(({ theme }) => ({
  padding: theme.spacing(3, 4),
  backgroundColor: '#fff',
  color: '#000',
  '@media print': {
    padding: 0,
  },
  '& .v5-MuiCheckbox-root': {
    padding: 0,
    color: '#000 !important',
  },
  '& .v5-MuiCheckbox-root.Mui-checked': {
    color: '#000 !important',
  },
  '& .v5-MuiCheckbox-root svg': {
    width: 18,
    height: 18,
  },
}));

const Title = styled('h1')(({ theme }) => ({
  fontSize: '1.6rem',
  fontWeight: 600,
  margin: theme.spacing(0, 0, 2, 0),
}));

const EnvChip = styled('span')(({ theme }) => ({
  fontSize: '0.95rem',
  opacity: 0.7,
  marginLeft: theme.spacing(2),
  fontWeight: 400,
}));

const Section = styled('section')(({ theme }) => ({
  marginBottom: theme.spacing(3),
  pageBreakInside: 'auto',
}));

const SectionTitle = styled('h2')(({ theme }) => ({
  fontSize: '1.05rem',
  fontWeight: 600,
  margin: theme.spacing(0, 0, 1, 0),
  pageBreakAfter: 'avoid',
}));

const Table = styled('table')(({ theme }) => ({
  width: '100%',
  // `fixed` is what makes column widths come from the <colgroup> below
  // instead of being derived from cell content. Without this, each
  // section's table picks its own widths and the region columns drift
  // horizontally between sections.
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  // Tightened from 0.8rem so all 9 columns fit on portrait without
  // wrapping the region headers (`AUE` etc) onto two lines.
  fontSize: '0.6rem',
  '& th, & td': {
    // Horizontal padding tightened too — every column saves ~8px and
    // those savings give the Step column real room on portrait.
    padding: theme.spacing(0.5, 0.5),
    verticalAlign: 'top',
    borderBottom: '1px solid #ccc',
    textAlign: 'left',
    // Allow long URLs / repo names to wrap inside the fixed cell width.
    wordBreak: 'break-word',
  },
  '& th': {
    fontWeight: 600,
    backgroundColor: '#f3f3f3',
    borderBottom: '1.5px solid #999',
  },
  // Cell-label spans (RegionTag in Current / region / preprod cells,
  // RegionDash for the empty `—` placeholder, plain `<span>` for the
  // missing-owner dash) all render at a tighter inline size on print so
  // 10-char tags like `2602.0-prod` stay on one line in a 36px region
  // column. The Checkbox span carries an MUI class so it's excluded —
  // shrinking the checkmark would defeat the printable-tick design.
  '& td > span:not([class*="MuiCheckbox"])': {
    fontSize: '0.55rem',
    opacity: 0.75,
    display: 'inline',
    marginTop: 0,
  },
}));

// Pixel widths tuned for A4/Letter portrait (~550px usable inner width
// after our own page padding). Step has no width so it absorbs the
// remainder. The fixed columns claim ~430px, leaving ~120px for Step
// text — tight but readable, and the rich-text wrap handles longer
// entries gracefully. State / Owner are allowed to wrap on really
// long content; Region headers are sized to keep `AUE` etc on one
// line, which is the most visually load-bearing constraint.
const ColStep = styled('col')({});
const ColState = styled('col')({ width: '64px' });
// Current carries `<XXXX>.0-prod` style tags — needs more room than a
// region cell but less than the State / Owner columns.
const ColCurrent = styled('col')({ width: '36px' });
const ColRegion = styled('col')({ width: '36px' });
// Preprod cells render either a checkbox or the actual tag label, so
// they need a touch more width than the plain region columns.
const ColPreprodRegion = styled('col')({ width: '54px' });
const ColOwner = styled('col')({ width: '100px' });

const RegionTh = styled('th')({ textAlign: 'center' });
const RegionTd = styled('td')({ textAlign: 'center' });

const StepHtml = styled('div')(({ theme }) => ({
  fontWeight: 500,
  lineHeight: 1.4,
  '& > *:first-child': { marginTop: 0 },
  '& > *:last-child': { marginBottom: 0 },
  '& p': { margin: theme.spacing(0.25, 0) },
  '& ul, & ol': {
    margin: theme.spacing(0.25, 0),
    paddingLeft: theme.spacing(3),
  },
  '& code': {
    fontFamily: 'monospace',
    fontSize: '0.7rem',
    backgroundColor: '#f3f3f3',
    padding: '0 4px',
    borderRadius: 3,
  },
  '& a': {
    color: '#000',
    textDecoration: 'underline',
  },
}));

const RepoName = styled('div')(({ theme }) => ({
  marginTop: theme.spacing(0.25),
  fontSize: '0.7rem',
  opacity: 0.7,
  fontFamily: 'monospace',
}));

const ParkedMessage = styled('td')({
  fontStyle: 'italic',
  opacity: 0.6,
  textAlign: 'center',
  verticalAlign: 'middle',
});

// Subtle greyscale tints for parked repo rows so the printed page surfaces
// "intentionally skipped" rows at a glance without relying on colour
// (good for B&W printers too). `colorAdjust: exact` forces browsers to
// keep the background when printing — by default the backgrounds are
// dropped to save ink.
const Row = styled('tr', {
  shouldForwardProp: prop => prop !== 'parked' && prop !== 'needsAttention',
})<{ parked: ParkedTint; needsAttention?: boolean }>(
  ({ parked, needsAttention }) => {
    // `needsAttention` overrides the parked-tint with a salmon shade so
    // the row stands out on screen and prints as a noticeably darker
    // band than the surrounding grey parked rows.
    if (needsAttention) {
      return {
        backgroundColor: '#f5c2c2',
        WebkitPrintColorAdjust: 'exact',
        printColorAdjust: 'exact',
      };
    }
    if (!parked) return {};
    return {
      backgroundColor: parked === 'not_ready' ? '#dcdcdc' : '#ececec',
      WebkitPrintColorAdjust: 'exact',
      printColorAdjust: 'exact',
    };
  },
);

/** @public */
export const ReleaseChecklistPrintPage = () => {
  const params = useParams();
  const release = params.release ? decodeURIComponent(params.release) : '';
  const { env, view, items, isFetching, isHydrating, error } = useBDirectRows(
    release || null,
  );
  const groups = useMemo(() => groupBySection(items), [items]);

  // Mirrors the live page's env-applicability filter: non-repo rows
  // whose template declares NO checkbox in the current env (every region
  // cell would be `—`) are dropped because they belong to the opposite
  // env's release (UAT-only step on a `*-prod` release, or vice versa).
  // Repo rows pass through — their region cells come from Azure tags,
  // not the template's `completed` schema.
  const envApplicable = (row: BDirectRow) => {
    if (row.repo) return true;
    return Object.values(row.completed).some(envs => envs?.[env] !== undefined);
  };

  // Set the document title — browsers seed the "Save as PDF" filename from
  // it, so `Release Checklist - 2603.0-prod.pdf` beats `localhost.pdf` as
  // a default. Restored on unmount in case the user navigates back inside
  // the same tab.
  useEffect(() => {
    if (!release) return;
    const previous = document.title;
    document.title = `Release Checklist - ${release}`;
    return () => {
      document.title = previous;
    };
  }, [release]);

  // Auto-open the browser print dialog once the data has rendered. Guarded
  // by a ref so re-renders (e.g. background refetches) don't keep popping
  // the dialog. We hold off until both `view` (the checklist itself) and
  // every downstream join query (builds, deployments, preprod, bitbucket
  // tags) have resolved — otherwise the print captures a half-loaded
  // table with `—` in every region / Current cell.
  // `requestAnimationFrame` gives layout one frame to settle so we don't
  // print a half-painted table; the user can still Ctrl+P later if they
  // cancel.
  const printedRef = useRef(false);
  useEffect(() => {
    if (printedRef.current) return;
    if (!view) return;
    if (isHydrating) return;
    printedRef.current = true;
    const id = requestAnimationFrame(() => window.print());
    return () => cancelAnimationFrame(id);
  }, [view, isHydrating]);

  if (!release) {
    return (
      <Root>
        <Typography>No release specified in URL.</Typography>
      </Root>
    );
  }
  if (isFetching && !view) return <Progress />;
  if (error) {
    return (
      <Root>
        <ResponseErrorPanel
          error={error}
          title="Fetching checklist"
          defaultExpanded
        />
      </Root>
    );
  }
  if (!view) return null;

  return (
    <Root>
      <Title>
        {release}
        <EnvChip>{env}</EnvChip>
      </Title>
      {groups.map((group, gi) => {
        const sectionId = group.section?.id ?? `orphan-${gi}`;
        const section = group.section ?? orphanSectionRow(sectionId);
        const visibleSteps = group.steps.filter(envApplicable);
        // Drop sections that have nothing left after env-filtering — a
        // section that's purely UAT-only steps shouldn't print at all on
        // a PROD release (and vice versa).
        if (visibleSteps.length === 0) return null;
        return (
          <Section key={sectionId}>
            <SectionTitle>{section.text || '(Untitled section)'}</SectionTitle>
            <Table>
              <colgroup>
                <ColStep />
                <ColState />
                <ColCurrent />
                {PREPROD_REGION_COLUMNS.map(r => (
                  <ColPreprodRegion key={`preprod-${r}`} />
                ))}
                {REGION_COLUMNS.map(r => (
                  <ColRegion key={r} />
                ))}
                <ColOwner />
              </colgroup>
              <thead>
                <tr>
                  <th>Step</th>
                  <th>State</th>
                  <th>Curr</th>
                  {PREPROD_REGION_COLUMNS.map(r => (
                    <RegionTh key={`preprod-${r}`}>{r}-PP</RegionTh>
                  ))}
                  {REGION_COLUMNS.map(r => (
                    <RegionTh key={r}>{r}</RegionTh>
                  ))}
                  <th>Owner</th>
                </tr>
              </thead>
              <tbody>
                {visibleSteps.map(row => {
                  const { isParked, tint, needsAttention, parkedMessage } =
                    rowDisplayMeta(row, release);
                  return (
                    <Row
                      key={row.id}
                      parked={tint}
                      needsAttention={needsAttention}
                    >
                      <td>
                        <StepHtml
                          // eslint-disable-next-line react/no-danger
                          dangerouslySetInnerHTML={{
                            __html: row.text || '<em>(empty step)</em>',
                          }}
                        />
                        {row.repo && <RepoName>{row.repo}</RepoName>}
                      </td>
                      <td>{row.repo ? STATE_LABEL[row.state] : '—'}</td>
                      <td>
                        <CurrentCell row={row} />
                      </td>
                      {isParked ? (
                        <ParkedMessage
                          colSpan={
                            PREPROD_REGION_COLUMNS.length +
                            REGION_COLUMNS.length
                          }
                        >
                          {parkedMessage}
                        </ParkedMessage>
                      ) : (
                        <>
                          {PREPROD_REGION_COLUMNS.map(r => (
                            <RegionTd key={`preprod-${r}`}>
                              <PreprodRegionCell
                                row={row}
                                region={r}
                                release={release}
                              />
                            </RegionTd>
                          ))}
                          {REGION_COLUMNS.map(r => (
                            <RegionTd key={r}>
                              <RegionCell
                                row={row}
                                region={r}
                                env={env}
                                release={release}
                                editing={false}
                              />
                            </RegionTd>
                          ))}
                        </>
                      )}
                      <td>
                        {row.owner ? (
                          refDisplay(row.owner)
                        ) : (
                          <span style={{ opacity: 0.5 }}>—</span>
                        )}
                      </td>
                    </Row>
                  );
                })}
              </tbody>
            </Table>
          </Section>
        );
      })}
    </Root>
  );
};
